import React from 'react';
import {
  RecentReportSerialized,
  RecentReportSerializer,
} from '@/recentReports/index';
import { useReportRedirect } from '@/reports/index';
import { PropertyTable } from '@/reports/property/sections/PropertyTable';
import {
  LizanoListInfo,
  LizanoMetaData,
} from '@/reports/sections/ListsSection/lists/lists';
import { styled, Button } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';

interface ListItemAllReportsTableProps {
  reports: LizanoListInfo['list_reports'];
}

const StyledReportTitleButton = styled(Button)(() => ({
  fontSize: '1rem',
  fontWeight: 'normal',
  textDecorationLine: 'underline',
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export function ListItemAllReportsTable({
  reports,
}: ListItemAllReportsTableProps) {
  const { redirectToReportUrl } = useReportRedirect();

  const [REPORT_TITLE, TYPE, CREATED_DATE, UPDATED_DATE] = [
    'reportTitle',
    'type',
    'createdDate',
    'updatedDate',
  ];

  const openReportRedirect = (reportType: ValidReportTypes, id: string) => {
    redirectToReportUrl(reportType, id);
  };

  const getReportTitleCell = ({
    reportData,
    detailsCityState,
    reportTypeFormatted,
    id,
  }: RecentReportSerialized) => {
    let reportTitleText = reportData;
    const reportType =
      reportTypeFormatted.toLocaleLowerCase() as ValidReportTypes;

    if (reportType === 'property') {
      reportTitleText = `${reportData}, ${detailsCityState}`;
    }

    return (
      <StyledReportTitleButton
        variant="text"
        color="primary"
        onClick={() => openReportRedirect(reportType, id)}
      >
        {reportTitleText}
      </StyledReportTitleButton>
    );
  };

  const generatePayload = (reportMeta: LizanoMetaData) => {
    if (!reportMeta?.report) return {};

    const serializedRecentReport = RecentReportSerializer(reportMeta.report);
    const { reportData, reportTypeFormatted, createdDate, updatedDate } =
      serializedRecentReport;

    const reportTitleCell = getReportTitleCell(serializedRecentReport);

    return {
      [REPORT_TITLE]: {
        accessor: reportData,
        displayData: reportTitleCell,
      },
      [TYPE]: {
        accessor: reportTypeFormatted,
        displayData: reportTypeFormatted,
      },
      [CREATED_DATE]: {
        accessor: createdDate,
        displayData: createdDate,
      },
      [UPDATED_DATE]: {
        accessor: updatedDate,
        displayData: updatedDate,
      },
    };
  };

  const tableRows = () => {
    if (!reports) return [];

    return reports.map((reportMeta, index) => ({
      id: index,
      payload: generatePayload(reportMeta),
    }));
  };

  const tableHeaders = [
    { id: REPORT_TITLE, label: 'Report' },
    { id: TYPE, label: 'Type' },
    { id: CREATED_DATE, label: 'Created' },
    { id: UPDATED_DATE, label: 'Updated' },
  ];

  return (
    <PropertyTable
      headers={tableHeaders}
      rows={tableRows()}
      config={{ showIndexColumn: true, isSortable: true }}
      customStyles={{
        '& .MuiTableCell-body': {
          textWrap: 'nowrap',
        },
        '& .MuiTableCell-root': {
          border: 'none',
        },
      }}
    />
  );
}
